import { SortDirection } from '@tanstack/react-table';
import styled, { css } from 'styled-components';

import { SafeMotionTr } from '../../../animation/SafeMotionTr';
import { rem } from '../../../helpers';

import { TABLE_ALIGN } from './DatatableCore.types';

export const $StyledTable = styled.table<{
  $withMinimalWidth: boolean;
  $isNewUiEnabled: boolean;
}>`
  min-width: ${({ $withMinimalWidth }) =>
    $withMinimalWidth ? undefined : '100%'};
  border-collapse: ${({ $isNewUiEnabled }) =>
    $isNewUiEnabled ? null : 'collapse'};
  border-color: ${({ $isNewUiEnabled, theme }) =>
    $isNewUiEnabled ? theme.newUI.variationColors.light3 : null};
  border-spacing: ${({ $isNewUiEnabled }) =>
    $isNewUiEnabled ? 'unset' : null};
  white-space: nowrap;
  width: 100%;
`;

export const $TableOrderContainer = styled.span``;
export const $TableContainer = styled.div<{
  $height?: number;
  $withMarginBottom: boolean;
}>`
  flex: 1;
  margin-bottom: ${({ $withMarginBottom }) =>
    $withMarginBottom ? '1rem' : null};
  position: relative;
  height: ${({ $height }) => ($height ? rem($height) : undefined)};
  overflow: auto;
`;

interface CellMixinProps {
  canSortBy?: boolean;
  align?: TABLE_ALIGN | string;
  $size?: number;
  $minSize?: number;
  $maxSize?: number;
  $isNewUiEnabled?: boolean;
  sort?: false | SortDirection;
}

const cellMixin = css<CellMixinProps>`
  display: ${({ $size }) => ($size === 0 ? 'none' : undefined)};
  width: ${({ $size }) => ($size ? `${$size}px` : undefined)};
  min-width: ${({ $minSize }) =>
    $minSize && $minSize >= 0 ? `${$minSize}px` : undefined};
  max-width: ${({ $maxSize }) =>
    $maxSize && $maxSize >= 0 ? `${$maxSize}px` : undefined};
  padding: ${rem(10)};
  text-align: ${({ align }) => align || TABLE_ALIGN.LEFT};
  font-size: ${({ theme }) => theme.fonts.size.megaSmall};

  &:last-child {
    padding-right: 0;
  }

  &:hover ${$TableOrderContainer} > svg {
    animation-iteration-count: ${({ canSortBy }) =>
      canSortBy ? 'infinite' : null};
  }
`;

export const $StyledTd = styled.td<CellMixinProps>`
  ${cellMixin};
  font-weight: ${({ theme }) => theme.fonts.weight.normal};
  color: ${({ theme }) => theme.colors.mainTextColor};
  vertical-align: middle;
  font-size: ${({ theme }) => theme.fonts.size.small};
`;

const thHover = css`
  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.newUI.variationColors.light5};
  }
`;

export const $StyledTh = styled.th<CellMixinProps>`
  ${cellMixin};

  ${({ canSortBy }) => (canSortBy ? thHover : null)}
  ${({ canSortBy, sort }) => {
    if (canSortBy && !!sort) {
      return css`
        background-color: ${({ theme }) => theme.newUI.variationColors.light5};

        & * {
          font-weight: ${({ theme }) => theme.fonts.weight.bold};
        }
        & svg {
          color: ${({ theme }) => theme.colors.primary};
        }
      `;
    }
  }}
  
  border-bottom: ${({ theme, $isNewUiEnabled }) =>
    $isNewUiEnabled
      ? theme.newUI.inputBorderColors.default
      : theme.colors.default} ${rem(1)} solid;
  padding-bottom: ${rem(10)};
  user-select: none;

  font-weight: ${({ theme, $isNewUiEnabled }) =>
    $isNewUiEnabled ? theme.fonts.weight.bold : theme.fonts.weight.mediumBold};

  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  > span {
    white-space: break-spaces;
  }
  line-break: strict;
  white-space: nowrap;

  & > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
`;

export const $StyledHeader = styled.div`
  text-transform: uppercase;
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
`;

export const $StyledTr = styled(SafeMotionTr)<{
  $isEven?: boolean;
  $isExpanded?: boolean;
  $isCliquable?: boolean;
  $isNewUiEnabled?: boolean;
  $isExpandedBackgroundEnabled?: boolean;
}>`
  position: relative;
  border-collapse: collapse;
  cursor: ${({ $isCliquable }) => ($isCliquable ? 'pointer' : 'default')};

  background-color: ${({
    theme,
    $isExpanded,
    $isNewUiEnabled,
    $isExpandedBackgroundEnabled,
  }) => {
    if (!$isExpandedBackgroundEnabled) {
      return undefined;
    }

    if ($isNewUiEnabled && $isExpanded) {
      return theme.newUI.variationColors.light6;
    }

    if ($isExpanded) {
      return theme.colors.datatableRowExpandedBackgroundColor;
    }

    return undefined;
  }};
`;
export const $SubTr = styled(SafeMotionTr)<{
  $isCliquable?: boolean;
  $isNewUiEnabled?: boolean;
  $isExpandedBackgroundEnabled?: boolean;
}>`
  position: relative;
  border-collapse: collapse;
  cursor: ${({ $isCliquable }) => ($isCliquable ? 'pointer' : 'default')};

  background-color: ${({
    theme,
    $isNewUiEnabled,
    $isExpandedBackgroundEnabled,
  }) => {
    if (!$isExpandedBackgroundEnabled) {
      return undefined;
    }

    return $isNewUiEnabled
      ? theme.newUI.variationColors.light6
      : theme.systemColors.secondaryBackground;
  }};
  padding: 0 1rem;
`;

export const $HeaderTr = styled.tr`
  position: relative;
  border-collapse: collapse;
`;

export const $StyledTHead = styled.thead<{
  $isNewUiEnabled?: boolean;
}>`
  position: sticky;
  top: 0;
  z-index: 10;

  ${$StyledTr} {
    vertical-align: bottom;
  }

  border-collapse: separate;
  background-color: ${({ theme }) => theme.systemColors.defaultBackground};
`;

export const $StyledTBody = styled.tbody<{
  $isNewUiEnabled?: boolean;
  $height?: number;
}>`
  ${$StyledTr} {
    &:not(:first-child) ${$StyledTd} {
      border-top: ${({ theme, $isNewUiEnabled }) =>
          $isNewUiEnabled
            ? theme.newUI.inputBorderColors.default
            : theme.colors.default}
        ${rem(1)} solid;
    }

    &:hover {
      background: ${({ theme, $isNewUiEnabled }) =>
        $isNewUiEnabled
          ? theme.newUI.variationColors.light6
          : theme.colors.datatableRowBackgroundHoverColor};
    }
  }
`;

export const $NoResultContainer = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
`;
